import React, { useState } from "react";
import { Table, Button, Dropdown, Menu, Modal, Tooltip, message, Pagination } from "antd";
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';

const SubDepartmentDataTable = ({ 
	rows, 
	handleEditRow, 
	handleDeleteRow,
	page,
	totalItems,
	onPageChange
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [menuIndex, setMenuIndex] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedRowId, setSelectedRowId] = useState(null);
	const [idTooltip, setIdTooltip] = useState("Copy ID");

	const handleMenuOpen = (e, index) => {
		setAnchorEl(e.currentTarget);
		setMenuIndex(index);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuIndex(null);
	};

	const handleCopyId = (e, id) => {
		e.stopPropagation();
		navigator.clipboard.writeText(id).then(() => {
			setIdTooltip("Copied!");
			setTimeout(() => setIdTooltip("Copy ID"), 1500);
		});
		message.success("ID copied to clipboard!");
	};

	const handleDeleteClick = (id) => {
		setSelectedRowId(id);
		setOpenDialog(true);
		handleMenuClose();
	};

	const handleDialogClose = () => {
		setOpenDialog(false);
		setSelectedRowId(null);
		handleMenuClose();
	};

	const handleConfirmDelete = async () => {
		await handleDeleteRow(selectedRowId);
		handleDialogClose();
	};

	const handlePrevPage = () => {
		if (page > 1) {
			onPageChange(page - 1);
		}
	};

	const handleNextPage = () => {
		const totalPages = Math.ceil(totalItems / 15);
		if (page < totalPages) {
			onPageChange(page + 1);
		}
	};

	const columns = [
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
			dataIndex: 'id',
			render: (text) => (
				<>
					{text.slice(-10)}
					<Tooltip title={idTooltip}>
						<Button
							icon={<CopyOutlined />}
							size="small"
							onClick={(e) => handleCopyId(e, text)}
							style={{ marginLeft: 8 }}
						/>
					</Tooltip>
				</>
			),
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Created At</span>,
			dataIndex: 'created_at',
			render: (text) => {
				if (!text) return "N/A";
				const date = new Date(text);
				date.setHours(date.getHours() + 5);
				date.setMinutes(date.getMinutes() + 30);
				const day = String(date.getDate()).padStart(2, '0');
				const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-based
			   const year = date.getFullYear();

			return `${day}/${month}/${year}`;
			},
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Organization</span>,
			dataIndex: 'organisation_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Department Name</span>,
			dataIndex: 'department_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Name</span>,
			dataIndex: 'name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Description</span>,
			dataIndex: 'description',
			render: (text) => (
				<Tooltip title={text} placement="top">
					<span>{text && text.length > 36 ? `${text.slice(0, 36)}...` : text || 'N/A'}</span>
				</Tooltip>
			),
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Head</span>,
			dataIndex: 'head',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>No of Roles</span>,
			dataIndex: 'no_roles_open',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
			key: 'actions',
			render: (_, record, index) => (
				<Dropdown
					overlay={
						<Menu>
							<Menu.Item onClick={() => handleEditRow(record.id)}>Edit</Menu.Item>
							<Menu.Item onClick={() => handleDeleteClick(record.id)}>Delete</Menu.Item>
						</Menu>
					}
					trigger={["click"]}
				>
					<Button icon={<MoreOutlined />} />
				</Dropdown>
			),
		},
	];

	return (
		<div style={{ margin: 10 }}>
			<Table
				columns={columns}
				dataSource={rows}
				rowKey="id"
				size="small"
				pagination={false}
				rowClassName="custom-row"
				className="antTableClass"
				style={{ 
					marginBottom: 0,
					borderRadius: '8px 8px 0 0'
				}} 
				scroll={{ x: 2000, y: "60vh" }}
			/>

			<div style={{ 
				display: 'flex', 
				justifyContent: 'space-between', 
				alignItems: 'center',
				padding: '16px 8px',
				background: 'rgba(238, 238, 240, 1)',
				borderTop: '0.5px solid rgba(218, 218, 221, 1)',
				borderRadius: '0 0 8px 8px',
				marginTop: '-10px'
			}}>
				<div style={{ 
					fontFamily: 'Inter', 
					fontSize: '12px', 
					fontWeight: 400, 
					lineHeight: '18px', 
					letterSpacing: '-0.1px', 
					color: '#27272A' 
				}}>
					Showing {Math.min(rows.length, 15)} of {totalItems} Sub-Departments
				</div>

				<Pagination
					current={page}
					total={totalItems}
					pageSize={15}
					showSizeChanger={false}
					size="small"
					style={{ margin: '0 auto' }}
					onChange={onPageChange}
					itemRender={(_, type, originalElement) => {
						if (type === 'prev' || type === 'next') {
							return null;
						}
						return originalElement;
					}}
					className="custom-pagination"
				/>

				<div style={{ display: 'flex', alignItems: 'center' }}>
					<div 
						onClick={handlePrevPage}
						style={{ 
							fontFamily: 'Inter', 
							fontSize: '12px', 
							fontWeight: 400, 
							lineHeight: '18px', 
							letterSpacing: '-0.1px', 
							color: page > 1 ? '#666' : '#ccc', 
							cursor: page > 1 ? 'pointer' : 'not-allowed', 
							marginRight: '0px',
							paddingRight: '16px' 
						}}
					>
						← Previous
					</div>
					<div 
						style={{ 
							borderLeft: '1px solid #d9d9d9',
							marginLeft: '8px',
							paddingLeft: '16px'
						}}
					>
						<div 
							onClick={handleNextPage}
							style={{ 
								fontFamily: 'Inter', 
								fontSize: '12px', 
								fontWeight: 400, 
								lineHeight: '18px', 
								letterSpacing: '-0.1px', 
								color: page < Math.ceil(totalItems / 15) ? '#666' : '#ccc', 
								cursor: page < Math.ceil(totalItems / 15) ? 'pointer' : 'not-allowed' 
							}}
						>
							Next →
						</div>
					</div>
				</div>
			</div>

			<Modal
				title="Confirm Deletion"
				visible={openDialog}
				onCancel={handleDialogClose}
				onOk={handleConfirmDelete}
				okText="Confirm"
				cancelText="Cancel"
			>
				<p>Are you sure you want to delete this item?</p>
			</Modal>
		</div>
	);
};

export default SubDepartmentDataTable;

<style jsx global>{`
	.custom-pagination .ant-pagination-item {
		border: 1px solid lightblue !important;
	}
	.custom-pagination .ant-pagination-item-active {
		border: 1px solid lightblue !important;
		background-color: #f0f0f0 !important;
	}
	.custom-pagination .ant-pagination-item:focus,
	.custom-pagination .ant-pagination-item:hover {
		border: 1px solid lightblue !important;
	}
	.custom-pagination .ant-pagination-item a {
		color: #666 !important;
	}
`}</style>
